import {
  ACTIVITY_IMAGE_ALLOWED_MIME_TYPES,
  ACTIVITY_IMAGE_MAX_FILE_SIZE_BYTES,
  type ActivityImageAllowedMimeType
} from 'constants/image';
import {
  DAY_PLAN_IMPORT_ALLOWED_MIME_TYPES,
  DAY_PLAN_IMPORT_MAX_FILE_SIZE_BYTES
} from 'constants/day-plan';

export const ALLOWED_IMAGE_TYPES = ACTIVITY_IMAGE_ALLOWED_MIME_TYPES;

export type AllowedImageType = ActivityImageAllowedMimeType;

export const MAX_ACTIVITY_IMAGE_SIZE_BYTES = ACTIVITY_IMAGE_MAX_FILE_SIZE_BYTES;

const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const DAY_PLAN_IMPORT_EXTENSION_MIME: Record<string, string> = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
  '.pdf': 'application/pdf',
  '.docx': DOCX_MIME
};

/** Prefer browser MIME; fall back to extension when `file.type` is empty (Windows). */
export const resolveDayPlanImportMimeType = (file: File): string => {
  if (file.type.length > 0) {
    return file.type;
  }
  const lowerName = file.name.toLowerCase();
  const extension = Object.keys(DAY_PLAN_IMPORT_EXTENSION_MIME).find((ext) =>
    lowerName.endsWith(ext)
  );
  return extension !== undefined
    ? DAY_PLAN_IMPORT_EXTENSION_MIME[extension]
    : '';
};
// null entries in a signature are wildcards (skip that byte position)
const MAGIC_BYTES: Record<string, (number | null)[][]> = {
  'image/jpeg': [[0xff, 0xd8, 0xff]],
  'image/png': [[0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
  'image/gif': [
    [0x47, 0x49, 0x46, 0x38, 0x39, 0x61],
    [0x47, 0x49, 0x46, 0x38, 0x37, 0x61]
  ],
  // WebP: RIFF at 0-3, 4 bytes of file size (variable), WEBP at 8-11
  'image/webp': [
    [0x52, 0x49, 0x46, 0x46, null, null, null, null, 0x57, 0x45, 0x42, 0x50]
  ],
  'application/pdf': [[0x25, 0x50, 0x44, 0x46]],
  // DOCX is a ZIP package (PK\x03\x04)
  [DOCX_MIME]: [[0x50, 0x4b, 0x03, 0x04]]
};

const matchesMagicBytes = (
  header: Uint8Array,
  signatures: (number | null)[][]
): boolean =>
  signatures.some((sig) =>
    sig.every((byte, index) => byte === null || header[index] === byte)
  );

const readFileHeader = (file: File, numBytes: number): Promise<Uint8Array> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const buf = e.target?.result;
      if (buf instanceof ArrayBuffer) {
        resolve(new Uint8Array(buf));
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = () => reject(new Error('FileReader error'));
    reader.readAsArrayBuffer(file.slice(0, numBytes));
  });

export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

const validateMagicBytes = async (
  file: File,
  mimeType: string
): Promise<FileValidationResult> => {
  const signatures = MAGIC_BYTES[mimeType];
  if (!signatures) {
    return { valid: true };
  }

  try {
    const header = await readFileHeader(file, 16);
    if (!matchesMagicBytes(header, signatures)) {
      return { valid: false, error: 'file_validation.content_mismatch' };
    }
  } catch {
    return { valid: false, error: 'file_validation.read_error' };
  }

  return { valid: true };
};

const validateAgainstAllowlist = async ({
  file,
  allowedMimeTypes,
  maxFileSizeBytes
}: {
  file: File;
  allowedMimeTypes: readonly string[];
  maxFileSizeBytes: number;
}): Promise<FileValidationResult> => {
  const mimeType = file.type;

  if (!allowedMimeTypes.includes(mimeType)) {
    return { valid: false, error: 'file_validation.invalid_type' };
  }

  if (file.size > maxFileSizeBytes) {
    return { valid: false, error: 'file_validation.too_large' };
  }

  if (!MAGIC_BYTES[mimeType]) {
    return { valid: false, error: 'file_validation.invalid_type' };
  }

  return validateMagicBytes(file, mimeType);
};

const validateImageAgainstAllowlist = async ({
  file,
  allowedMimeTypes
}: {
  file: File;
  allowedMimeTypes: readonly string[];
}): Promise<FileValidationResult> =>
  validateAgainstAllowlist({
    file,
    allowedMimeTypes,
    maxFileSizeBytes: ACTIVITY_IMAGE_MAX_FILE_SIZE_BYTES
  });

export const validateImageFile = async (
  file: File
): Promise<FileValidationResult> =>
  validateImageAgainstAllowlist({
    file,
    allowedMimeTypes: ACTIVITY_IMAGE_ALLOWED_MIME_TYPES
  });

/** Day Plan calendar import: jpeg/png/webp/pdf/docx (no GIF). */
export const validateDayPlanImportFile = async (
  file: File
): Promise<FileValidationResult> => {
  const mimeType = resolveDayPlanImportMimeType(file);
  return validateAgainstAllowlist({
    file: mimeType.length > 0 && file.type.length === 0
      ? new File([file], file.name, { type: mimeType })
      : file,
    allowedMimeTypes: DAY_PLAN_IMPORT_ALLOWED_MIME_TYPES,
    maxFileSizeBytes: DAY_PLAN_IMPORT_MAX_FILE_SIZE_BYTES
  });
};
export const validateAttachmentFile = async (
  file: File
): Promise<FileValidationResult> => {
  const ALLOWED_ATTACHMENT_TYPES = [
    ...ACTIVITY_IMAGE_ALLOWED_MIME_TYPES,
    'application/pdf',
    'text/plain',
    'application/msword',
    DOCX_MIME,
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ];

  const mimeType = file.type;

  if (!ALLOWED_ATTACHMENT_TYPES.includes(mimeType)) {
    return { valid: false, error: 'file_validation.invalid_type' };
  }

  return validateMagicBytes(file, mimeType);
};
